import { Flex, Grid, Spacer } from '@zillow/constellation';
import { Box, Button, Heading, Text } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';

import { DISPLAY_CONTEXT } from '../PaymentGateway/constants';

import { PREMIUM_FEATURES_BY_CONTEXT } from '../../constants/FreemiumConstants';

import LearnAboutPremiumButton, { RENDER_AS_TYPE } from '../LearnAboutPremiumButton';
import PremiumProductOptionsSimple from '../PremiumProductOptionsSimple';

import { StyledBgFullWidth, StyledContentContainer } from '../StyledComponents/StyledLayout';

type PremiumFeatureSummaryProps = {
  componentContext?: string;
  displayContentFullWidth?: boolean;
  hideCompareAndLearnMore?: boolean;
  offsetPageWizardTopMargin?: boolean;
  onPublishClick?: () => void;
  paddingTop?: string;
  premiumCompareRef?: React.RefObject<HTMLDivElement>;
};

const PremiumFeatureSummary = ({
  componentContext = DISPLAY_CONTEXT.MODULAR_LISTING,
  displayContentFullWidth = false,
  hideCompareAndLearnMore = false,
  onPublishClick,
  offsetPageWizardTopMargin = true,
  paddingTop = 'lg',
  premiumCompareRef,
}: PremiumFeatureSummaryProps) => {
  const gridColumnEndLeftColumn = displayContentFullWidth
    ? { default: 'span 6', lg_gte: 'span 12' }
    : { default: 'span 6', lg_gte: 'span 12', xxl_gte: 'span 7' };

  const gridColumnEndRightColumn = displayContentFullWidth
    ? { default: 'span 6', lg_gte: 'span 12' }
    : { default: 'span 6', lg_gte: 'span 12', xxl_gte: 'span 5' };

  const executeScroll = () => {
    if (premiumCompareRef?.current) {
      const element = premiumCompareRef.current;

      try {
        // StyledContentContainer in PageWizard
        const targetElement = element.parentElement?.parentElement?.parentElement?.parentElement?.parentElement;

        if (!targetElement) {
          throw new Error('Could not find parent element for scroll.');
        }

        const offsetPosition = element.offsetTop - 100;
        targetElement.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      } catch (error) {
        logError({ error, context: 'PremiumFeatureSummary.executeScroll' });
        console.error('ERROR:', error);
      }
    }
  };

  const handleCompareOptionsClick = () => {
    executeScroll();
  };

  const renderPremiumUpsellItemList = () => {
    return Object.entries(PREMIUM_FEATURES_BY_CONTEXT).map(([key, value]) => {
      const { HEADING, BODY, ICON: Icon } = value.PAY_AND_PUBLISH;

      return (
        <Flex key={key} display="flex" flexDirection="row" marginBottom="md">
          {/* need div container to prevent mismatching icon sizes */}
          <div>
            <Icon size={{ default: 'md' }} marginRight={{ default: 'sm' }} />
          </div>

          <Spacer>
            <Text textStyle="body-bold" asChild>
              <p>{HEADING}</p>
            </Text>
            <Text asChild>
              <p>{BODY}</p>
            </Text>
          </Spacer>
        </Flex>
      );
    });
  };

  return (
    <Box
      data-testid="PremiumFeatureSummary"
      css={{
        position: 'relative',
        marginTop: offsetPageWizardTopMargin ? '-32px' : undefined,
      }}
    >
      <StyledBgFullWidth>
        <StyledContentContainer>
          <Spacer padding={0} paddingTop={paddingTop}>
            <Grid
              display="grid"
              gridTemplateColumns={{
                default: 'repeat(6, 1fr)',
                // 769
                lg_gte: 'repeat(12, 1fr)',
              }}
              gap="sm"
            >
              <Grid gridColumnEnd={gridColumnEndLeftColumn}>
                <Heading level={1} textStyle="heading-md" fontFamily="heading-alt" css={{ marginBottom: '600' }}>
                  Your listing is active! Get the most with premium.
                </Heading>

                <Spacer data-testid="PremiumUpsellDetailsContainer" marginBottom="md">
                  {renderPremiumUpsellItemList()}
                </Spacer>

                {!hideCompareAndLearnMore && (
                  <Flex display="flex" flexDirection="row" marginBottom="xl">
                    <Button
                      tone="brand"
                      emphasis="outlined"
                      css={{ marginRight: '800' }}
                      onClick={handleCompareOptionsClick}
                    >
                      Compare your options
                    </Button>
                    <LearnAboutPremiumButton as={RENDER_AS_TYPE.TEXT_BUTTON}>Learn more</LearnAboutPremiumButton>
                  </Flex>
                )}
              </Grid>

              <Grid gridColumnEnd={gridColumnEndRightColumn} justifyContent="center">
                <Spacer marginBottom="md">
                  <PremiumProductOptionsSimple componentContext={componentContext} onPublishClick={onPublishClick} />
                </Spacer>
              </Grid>
            </Grid>
          </Spacer>
        </StyledContentContainer>
      </StyledBgFullWidth>
    </Box>
  );
};

export default PremiumFeatureSummary;
